/**
 * Design canvas with a fixed height and a fluid width, scaled to fill any TV viewport.
 *
 * The scale comes from the height alone, so the vertical design space is identical on every
 * screen and nothing can fall out of frame. The canvas width follows the viewport aspect
 * ratio, which leaves no letterbox bars and gives wider screens more horizontal room.
 */

import { ReactNode, useLayoutEffect, useRef, useState } from 'react'

const CANVAS_HEIGHT = 900

interface TvCanvasProps {
  children: ReactNode
}

export function TvCanvas({ children }: TvCanvasProps) {
  const outerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [canvasWidth, setCanvasWidth] = useState(CANVAS_HEIGHT * (16 / 9))

  useLayoutEffect(() => {
    const outer = outerRef.current
    if (outer === null) {
      return
    }

    const applyScale = () => {
      const { width, height } = outer.getBoundingClientRect()
      if (width === 0 || height === 0) {
        return
      }
      const nextScale = height / CANVAS_HEIGHT
      setScale(nextScale)
      setCanvasWidth(width / nextScale)
    }

    applyScale()

    const observer = new ResizeObserver(applyScale)
    observer.observe(outer)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={outerRef}
      className="w-full h-screen overflow-hidden bg-background flex items-center justify-center"
    >
      <div
        style={{
          width: canvasWidth,
          height: CANVAS_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'center',
          flexShrink: 0,
        }}
      >
        {children}
      </div>
    </div>
  )
}
